import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventJobRunning } from '../../hooks/useEventJobRunning';
import { useEventSyncStatusQuery } from '../../types/planung-graphql-client-defs';
import styles from './EventSyncStatusLine.module.scss';

/** So oft wird nachgesehen, solange Tage warten oder ein Abgleich läuft. */
const REFRESH_MS = 10_000;

const formatDay = (day: string) => dayjs(day).format('DD.MM.YYYY');

/**
 * Über der Liste der Synchronisationen: wie viele Tage auf den Termin-Abgleich warten und welche Versionen in Korrektur
 * davon wie viele zurückhalten (planung-backend, docs/plans/2026-11-03-correction-visibility-plan.md). Die Versionen
 * liefert das Backend nur mit Modul Stundenplan und Leserecht; ohne offene Tage keine Zeile.
 */
export const EventSyncStatusLine = () => {
  const { t } = useTranslation();
  const isRunning = useEventJobRunning();
  const [{ data }, refetch] = useEventSyncStatusQuery({ requestPolicy: 'network-only' });
  const status = data?.eventSyncStatus;
  const waiting = (status?.openDays ?? 0) > 0;

  useEffect(() => {
    if (!waiting && !isRunning) return;
    const id = setInterval(() => refetch({ requestPolicy: 'network-only' }), REFRESH_MS);
    return () => clearInterval(id);
  }, [waiting, isRunning, refetch]);

  if (!status || !waiting || !status.firstOpenDay || !status.lastOpenDay) return null;

  const period =
    status.firstOpenDay === status.lastOpenDay
      ? formatDay(status.firstOpenDay)
      : `${formatDay(status.firstOpenDay)} – ${formatDay(status.lastOpenDay)}`;

  return (
    <div className={styles.status}>
      <p>{t('synchronizations.openDays', { count: status.openDays, period })}</p>
      {status.versionsInCorrection
        .filter((version) => version.heldDays > 0)
        .map((version) => (
          <p key={version.versionUuid}>
            {t('synchronizations.heldBy', {
              count: version.heldDays,
              plan: version.planName,
              version: version.version,
            })}
          </p>
        ))}
    </div>
  );
};
